import React, { useState ,useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './About.module.css';
import { useTranslation } from 'react-i18next';
import PropertySwiper from "../components/PropertySwiper";
import SearchIcon from '../images/search.svg';
import MeterIcon from '../images/meter.png';
import BedroomsIcon from '../images/bedrooms.png';
import AddressIcon from '../images/address.png';
import PriceIcon from '../images/price.png';
import UnitsIcon from '../images/units.png';
import { authFetch } from '../utils/authFetch';
import { BASE_URL } from '../utils/config';
import apartmentImage from '../images/sponer.svg';
import { useAuth } from '../context/AuthContext';
import { useFlow } from '../context/FlowContext';

const About = () => {
  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to format numbers with apostrophes (Swiss format)
  const formatWithApostrophe = (value) => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/'/g, '')) : value;
    if (isNaN(numValue)) return value;
    const digitsOnly = numValue.toString().replace(/[^\d]/g, '');
    return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  };

  const { t } = useTranslation();
  const { isAuthenticated, currentUser } = useAuth();
  const { currentFlow } = useFlow();

  const [activeTab, setActiveTab] = useState('about');
  const [offers, setOffers] = useState([]);
  const [demands, setDemands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sliderKey, setSliderKey] = useState(Date.now());

  // Determine user type
  const isOfferUser = currentFlow === "offer" || currentUser?.userType === "offer";
  const isDemandUser = currentFlow === "demand" || currentUser?.userType === "demand";

  useEffect(() => {
    // Only fetch data if user is logged in
    if (!isAuthenticated) {
      setOffers([]);
      setDemands([]);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        let endpoint = '';
        if (isOfferUser) {
          endpoint = `${BASE_URL}/property/latest/offers?limit=30`;
        } else if (isDemandUser) {
          endpoint = `${BASE_URL}/property/latest/demands?limit=30`;
        } else {
          // If user type is not determined, don't fetch
          setOffers([]);
          setDemands([]);
          setIsLoading(false);
          return;
        }

        const response = await authFetch(endpoint);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${isOfferUser ? 'offers' : 'demands'}`);
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await response.json();
          if (isOfferUser) {
            setOffers(data);
            setDemands([]);
          } else {
            setDemands(data);
            setOffers([]);
          }
          setSliderKey(Date.now()); 
        } else {
          const text = await response.text();
          throw new Error(`Server returned a non-JSON response. Check your API route. Response was: "${text.substring(0, 50)}..."`);
        }
      } catch (err) {
        setError(err.message);
        console.error(`Error fetching ${isOfferUser ? 'offers' : 'demands'}:`, err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // const intervalId = setInterval(fetchData, 60000);

    return () => {
      // Polling disabled to prevent property changes during animation
      // clearInterval(intervalId);
    };
  }, [isAuthenticated, isOfferUser, isDemandUser]);



  const renderContent = () => {
    switch (activeTab) {
      case 'about':
        return (

          <div>
            <h3 className={`${styles.paragraph} ${styles.boldText} ${styles.largeHeading}`}>
              {t('about_heading_perfect_match')}
            </h3>
            <p className={`${styles.paragraph} ${styles.boldText}`}>
              {t('about_para1_tired_of_ads')}
            </p>
            <p className={styles.paragraph}>
              {t('about_para2_search_long_frustrating')}
            </p>
            <p className={`${styles.paragraph} ${styles.boldText}`}>
              {t('about_para3_innovative_portal')}
            </p>
            <p className={styles.paragraph}>
              {t('about_para4_advanced_algorithms_part1')}
              <span className={styles.highlightText}>search.casa</span>
              {t('about_para4_advanced_algorithms_part2')}
            </p>
            <h3 className={styles.subHeader}>{t('about_how_it_works_heading')}</h3>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('about_how_it_works_item1_heading')}</span>
              <span className={styles.inlineText}>{t('about_how_it_works_item1_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('about_how_it_works_item2_heading')}</span>
              <span className={styles.inlineText}>{t('about_how_it_works_item2_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('about_how_it_works_item3_heading')}</span>
              <span className={styles.inlineText}>{t('about_how_it_works_item3_text')}</span>
            </div>

            <div>
              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_how_it_works_item4_heading')}</span>
                <span className={styles.inlineText}>
                  {t('about_how_it_works_item4_text_part1')}
                  <span className={styles.highlightText}>search-casa</span>
                  {t('about_how_it_works_item4_text_part2')}
                  <span className={styles.highlightText}>search-casa</span>
                  {t('about_how_it_works_item4_text_part3')}
                </span>
              </div>

              <h3 className={styles.subHeader}>{t('about_advantages_heading')}</h3>

              <h3 className={styles.subHeader}>{t('about_for_home_seekers_heading')}</h3>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_home_seekers_item1_heading')}</span>
                <span className={styles.inlineText}>{t('about_home_seekers_item1_text')}</span>
              </div>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_home_seekers_item2_heading')}</span>
                <span className={styles.inlineText}>{t('about_home_seekers_item2_text')}</span>
              </div>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_home_seekers_item3_heading')}</span>
                <span className={styles.inlineText}>{t('about_home_seekers_item3_text')}</span>
              </div>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_home_seekers_item4_heading')}</span>
                <span className={styles.inlineText}>{t('about_home_seekers_item4_text')}</span>
              </div>

              <h3 className={styles.subHeader}>{t('about_for_owners_heading')}</h3>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_owners_item1_heading')}</span>
                <span className={styles.inlineText}>{t('about_owners_item1_text')}</span>
              </div>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_owners_item2_heading')}</span>
                <span className={styles.inlineText}>{t('about_owners_item2_text')}</span>
              </div>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_owners_item3_heading')}</span>
                <span className={styles.inlineText}>{t('about_owners_item3_text')}</span>
              </div>

              <div className={styles.inlineContent}>
                <span className={styles.inlineHeading}>{t('about_owners_item4_heading')}</span>
                <span className={styles.inlineText}>{t('about_owners_item4_text')}</span>
              </div>

              <p className={styles.paragraph}>
                {t('about_closing_para_part1')}
                <span className={styles.highlightText}>search.casa</span>
                {t('about_closing_para_part2')}
              </p>

              <h4 className={styles.subHeader}>{t('about_cta_question')}</h4>
              <p className={styles.paragraph}>
                <span className={styles.ctaText}>{t('about_cta_text_part1')}</span>
                {t('about_cta_text_part2_part1')}
                <span className={styles.highlightText}>search.casa</span>
                {t('about_cta_text_part2_part2')}
              </p>
            </div>
          </div>
        );
      case 'buttonOne':
        return (
          <div>
            <h3 className={`${styles.paragraph} ${styles.boldText} ${styles.largeHeading}`}>
              {t('button_one_heading_dream_home')}
            </h3>
            <p className={`${styles.paragraph} ${styles.boldText}`}>
              {t('button_one_para1_tired_scrolling')}
            </p>
            <p className={styles.paragraph}>
              {t('button_one_para2_search_casa_past_part1')}
              <span className={styles.highlightText}>search-casa</span>
              {t('button_one_para2_search_casa_past_part2')}
            </p>
            <p className={`${styles.paragraph} ${styles.boldText}`}>
              {t('button_one_why_choose_heading_part1')}
              <span className={styles.highlightText}>search-casa</span>
              {t('button_one_why_choose_heading_part2')}
            </p>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_one_item1_heading')}</span>
              <span className={styles.inlineText}>{t('button_one_item1_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_one_item2_heading')}</span>
              <span className={styles.inlineText}>{t('button_one_item2_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_one_item3_heading')}</span>
              <span className={styles.inlineText}>{t('button_one_item3_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_one_item4_heading')}</span>
              <span className={styles.inlineText}>{t('button_one_item4_text')}</span>
            </div>

            <p className={styles.paragraph}>
              {t('button_one_testimonial_part1')}
              <span className={styles.highlightText}>search-casa</span>
              {t('button_one_testimonial_part2_part1')}
              <span className={styles.highlightText}>- Marco, Thalwil</span>
            </p>
            <h4 className={styles.subHeader}>{t('button_one_cta_heading')}</h4>
          </div>
        );
      case 'buttonTwo':
        return (
          <div>
            <h3 className={`${styles.paragraph} ${styles.boldText} ${styles.largeHeading}`}>
              {t('button_two_heading_sell_rent')}
            </h3>
            <p className={`${styles.paragraph} ${styles.boldText}`}>
              {t('button_two_para1_tired_calls')}
            </p>
            <p className={styles.paragraph}>
              <span className={styles.highlightText}>search-casa</span>
              {t('button_two_para2_search_casa_revolutionises_part2')}
            </p>
            <p className={`${styles.paragraph} ${styles.boldText}`}>
              {t('button_two_advantages_heading')}
            </p>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_two_item1_heading')}</span>
              <span className={styles.inlineText}>{t('button_two_item1_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_two_item2_heading')}</span>
              <span className={styles.inlineText}>{t('button_two_item2_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_two_item3_heading')}</span>
              <span className={styles.inlineText}>{t('button_two_item3_text')}</span>
            </div>

            <div className={styles.inlineContent}>
              <span className={styles.inlineHeading}>{t('button_two_item4_heading')}</span>
              <span className={styles.inlineText}>{t('button_two_item4_text')}</span>
            </div>

            <p className={styles.paragraph}>
              {t('button_two_testimonial_part1')}
              <span className={styles.highlightText}>search-casa</span>
              {t('button_two_testimonial_part2_part1')}
              <span className={styles.highlightText}>- Laura, Lausanne</span>
            </p>
            <h4 className={styles.subHeader}>{t('button_two_cta_heading')}</h4>
          </div>
        );
      default:
        return null;
    }
  };
  const displayedOffers = offers.slice(0, 3);
  const displayedDemands = demands.slice(0, 3);
  
  // Create offers with sponsor inserted after first offer
  const offersWithSponsor = React.useMemo(() => {
    if (!displayedOffers || displayedOffers.length === 0) return [];
    const items = [...displayedOffers];
    // Insert sponsor after first offer
    items.splice(1, 0, {
      id: "sponsor",
      isSponsor: true,
    });
    return items;
  }, [displayedOffers]);

  // Create demands with sponsor inserted after first demand
  const demandsWithSponsor = React.useMemo(() => {
    if (!displayedDemands || displayedDemands.length === 0) return [];
    const items = [...displayedDemands];
    // Insert sponsor after first demand
    items.splice(1, 0, {
      id: "sponsor",
      isSponsor: true,
    });
    return items;
  }, [displayedDemands]);

  return (
    <div className={styles.container}>
      {/* Only show left panel if user is logged in */}
      <div className={`${styles.rightPanel} ${!isAuthenticated ? styles.fullWidth : ''}`}>
        <div className={styles.tabsContainer}>
          <button
            className={`${styles.tabButton} ${activeTab === 'about' ? styles.activeTab : styles.inactiveTab}`}
            onClick={() => setActiveTab('about')}
          >
            Search.casa
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'buttonOne' ? styles.activeTab : styles.inactiveTab}`}
            onClick={() => setActiveTab('buttonOne')}
          >
            For Home Seekers
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'buttonTwo' ? styles.activeTab : styles.inactiveTab}`}
            onClick={() => setActiveTab('buttonTwo')}
          >
            For The Owners
          </button>
        </div>

        <div className={styles.contentContainer}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default About;
